'use client';

import { API_ROUTES, useApiService } from '@shared/api-utils';
import { useClientConfig } from '@shared/utils';
import { usePathname } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerProfile, Session } from '../types';
import { AuthGuard } from './AuthGuard/AuthGuard';

const SessionContext = createContext<{
  session: Session;
  update: () => Promise<Session>;
}>({
  session: { profile: null, status: 'unauthenticated' },
  update: () => Promise.resolve({ profile: null, status: 'unauthenticated' }),
});

function SessionProvider({ children, initialSession }: { children: React.ReactNode; initialSession?: Session }) {
  const [session, setSession] = useState<Session>(initialSession ?? { profile: null, status: 'uninitialised' });
  const updateCounterRef = useRef(0);
  const hasFetchedRef = useRef(false);
  const inFlightSessionUpdateRef = useRef<Promise<Session> | null>(null);
  const { apiKey } = useClientConfig();
  const apiService = useApiService();

  const validateSession = useCallback(async (): Promise<Session> => {
    try {
      const response = await apiService.get<PlayerProfile>(API_ROUTES.MY_ACCOUNT.PLAYER_PROFILE.GET, {
        headers: {
          'x-api-key': apiKey,
        },
        credentials: 'include',
        cache: 'no-store',
      });

      if (response.error) {
        return {
          profile: null,
          status: 'unauthenticated',
        };
      }

      const playerProfile = response.data;

      return {
        profile: playerProfile,
        status: 'authenticated',
      };
    } catch {
      return {
        profile: null,
        status: 'unauthenticated',
      };
    }
  }, [apiService, apiKey]);

  const update = useCallback(async (): Promise<Session> => {
    if (inFlightSessionUpdateRef.current) {
      return inFlightSessionUpdateRef.current;
    }

    // Only degrade to 'loading' on the very first session resolution (status === 'uninitialised').
    // Subsequent refetches (after registration/login/logout/etc.) preserve the previous status
    // so AuthGuard does not unmount its children mid-flight, which would otherwise destroy
    // in-flight UI state such as multistep forms, opened sheets/dialogs and refs.
    setSession(prev => (prev.status === 'uninitialised' ? { ...prev, status: 'loading' } : prev));

    const promise = (async (): Promise<Session> => {
      const callId = ++updateCounterRef.current;

      const currentSession = await validateSession();

      // Discard stale results: if a newer update() call has started since this one, its result
      // will be applied instead. This prevents an older in-flight request (e.g. sent with an
      // expired token) from overwriting a more recent result (e.g. sent after token refresh).
      setSession(prev => (callId === updateCounterRef.current ? currentSession : prev));

      return currentSession;
    })();

    inFlightSessionUpdateRef.current = promise;

    try {
      return await promise;
    } finally {
      inFlightSessionUpdateRef.current = null;
    }
  }, [validateSession]);

  // Fetch session on mount when no initial session was provided (client-side only auth).
  // Tests inject `initialSession` to bypass validation.
  useEffect(() => {
    if (initialSession) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    update();
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      session,
      update,
    }),
    [session, update]
  );

  return (
    <SessionContext.Provider value={contextValue}>
      <AuthGuard>{children}</AuthGuard>
      <SessionSyncAfterRegistration />
    </SessionContext.Provider>
  );
}

function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  const sessionRef = useRef(context.session);
  useEffect(() => {
    sessionRef.current = context.session;
  });

  const resolveProfile = useCallback(async () => {
    const current = sessionRef.current;
    if (current.status === 'authenticated' && current.profile) {
      return current.profile;
    }
    const updated = await context.update();
    return updated?.profile ?? null;
  }, [context.update]);

  return {
    ...context,
    isAuthenticated: context.session.status === 'authenticated',
    isLoading: context.session.status === 'uninitialised' || context.session.status === 'loading',
    resolveProfile,
  };
}

function SessionSyncAfterRegistration() {
  const pathname = usePathname();
  const { update, isAuthenticated, isLoading } = useSession();
  const hasSyncedRef = useRef(false);

  // Reading the param from window.location after mount (instead of useSearchParams)
  // keeps this layout-level component from bailing every page to client-side rendering.
  // See docs/guides/avoiding-csr-bailout.md (Option 1).
  useEffect(() => {
    if (hasSyncedRef.current) return;
    if (new URLSearchParams(window.location.search).get('registration-success') !== 'true') return;
    if (isAuthenticated) return;
    if (isLoading) return;

    hasSyncedRef.current = true;
    void update();
  }, [pathname, isAuthenticated, isLoading, update]);

  return null;
}

export { SessionContext, SessionProvider, useSession };
